<template>
  <div class="relative min-w-[360px] min-h-[320px] h-full">
    <div
      class="absolute right-2 top-0 w-10 h-5 z-20 pointer-events-none flex"
      :style="{
        background: backgroundColor && backgroundColor !== 'transparent' ? backgroundColor : '#F0F5F8'
      }">
      <Download :color="textColor" class="ml-auto" />
    </div>

    <apexchart :id="chartid"
               :options="chart_options"
               :series="datasets"
               :height="115 * height"
               width="100%"
               @click="clickHandler" />
  </div>
</template>

<script>
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { Download } from "lucide-vue-next"

export default {
  name: "Chart",
  components: {
    Download
  },
  props: {
    textColor: null,
    showDataLabels: null,
    backgroundColor: null,
    axis: {
      type: Object,
      required: false
    },
    chartid: {
      type: String,
      default: "chartName"
    },
    chartdata: {
      type: Array,
      default: () => [110, 90, 80, 70, 60, 50, 40, 35, 30, 25, 15]
    },
    labels: {
      type: Array,
      default: () => []
    },
    hasLegend: {
      type: Boolean,
      default: true
    },
    showValues: {
      type: Boolean,
      default: true
    },
    showZoom: {
      type: Boolean,
      default: true
    },
    graphObj: {
      type: Object,
      required: true
    },
    height: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      column_colors: [],
      chart_options: {},
      formattedChartData: [],
      toFixedChartData: [],
      formattedLables: [],
      datasets: []
    }
  },
  computed: {
    changed() {
      return this.$store.state.ui.darkMode
    },
    isChecked() {
      return this.hasLegend
    }
  },
  watch: {
    changed() {
      this.generateColors(this.changed)
    }
  },
  created() {
    this.formattedChartData = this.labels
    this.chartdata.map((x) => {
      this.toFixedChartData.push(x.data.map((point) => this.formatToFixed(point)))
      this.datasets.push({
        ...x,
        name: x.label,
        borderRadius: 13,
        minBarLength: 10,
        borderSkipped: true,
        data: x.data.map((point) => this.formatToFixed(point))
      })
      this.column_colors.push(x.backgroundColor[0])
    })
    this.formattedLables = this.labels.map((point) => (Number(point) ? this.formatPoint(Number(point)) : point))
    this.createChart()
  },
  methods: {
    clickHandler(event, chartContext, config) {
      if (config.dataPointIndex != -1) {
        let cleaned_filter = ""
        if (this.graphObj.filter) {
          cleaned_filter = this.refineFilterString(this.graphObj, config.seriesIndex)
        } else {
          cleaned_filter = this.graphObj.filter
        }
        this.$emitter.emit(ALL_EMITTERS.GRAPH_DETAIL_MODAL, {
          graph_label_value: this.formattedLables[config.dataPointIndex],
          graph_rollup_metric: this.graphObj.series[config.seriesIndex].metric,
          graph_metric: this.axis,
          filter: cleaned_filter
        })
      }
    },
    refineFilterString(graph_obj, seriesIndex) {
      const series_clicked = graph_obj.series[seriesIndex]
      const ref_names_of_metrics_to_be_removed = graph_obj.series
        .filter((o) => o.metric.reference_name !== series_clicked.metric.reference_name)
        .map((g) => g.metric.reference_name)
      const filter_array = graph_obj.filter.split(" ")
      let index_counter = 0

      const cleaned_filter_array = []
      const cleaned_filter_array_2 = []
      let cleaned_filter_string = ""

      while (index_counter <= filter_array.length) {
        const filter_item = filter_array[index_counter]
        if (ref_names_of_metrics_to_be_removed.includes(filter_item)) {
          index_counter += 3
        } else {
          index_counter += 1
          if (filter_item && index_counter <= filter_array.length) {
            cleaned_filter_array.push(filter_item)
          }
        }
      }
      cleaned_filter_array.forEach((filter_item, i) => {
        if (filter_item == "and" || filter_item == "or") {
          if (
            (this.$utils.getMetricByReferenceName(cleaned_filter_array[i + 1]) || cleaned_filter_array[i + 1] === "(") &&
            (this.$utils.getMetricByReferenceName(cleaned_filter_array[i - 3]) || cleaned_filter_array[i - 1] === ")")
          ) {
            cleaned_filter_array_2.push(filter_item)
          }
        } else {
          cleaned_filter_array_2.push(filter_item)
        }
      })
      cleaned_filter_string = [...cleaned_filter_array_2].join(" ").replace("( ) or", "")
      cleaned_filter_string = cleaned_filter_string.replace("( ) and", "")
      cleaned_filter_string = cleaned_filter_string.replace("or ( )", "")
      cleaned_filter_string = cleaned_filter_string.replace("and ( )", "")

      return cleaned_filter_string
    },
    formatToFixed(datapoint) {
      return Number(datapoint).toFixed(2)
    },
    formatPoint(datapoint) {
      return this.$utils.truncateNumber(datapoint)
    },
    createChart() {
      this.chart_options = {
        theme: {
          mode: this.$store.state.ui.darkMode ? "dark" : "light"
        },
        grid: {
          borderColor: "#dbdbdb"
        },
        title: {
          show: true,
          text: this.graphObj.name,
          align: "center"
        },
        legend: {
          showForSingleSeries: true
        },
        chart: {
          foreColor: this.textColor,
          width: 100,
          background: this.$store.state.ui.darkMode ? "#2b3c44" : "transparent",
          type: "area",
          zoom: {
            enabled: this.showZoom
          },
          toolbar: {
            export: {
              csv: {
                filename: this.graphObj.name
              },
              svg: {
                filename: this.graphObj.name
              },
              png: {
                filename: this.graphObj.name
              }
            },
            autoSelected: "zoom"
          }
        },
        colors: this.column_colors,
        dataLabels: {
          enabled: this.showDataLabels
        },
        stroke: {
          curve: "straight"
        },
        xaxis: {
          categories: this.labels.length ? this.formattedLables : this.formattedChartData,
          labels: {
            show: this.isChecked,
            style: {
              colors: this.$store.state.ui.darkMode ? "white" : this.textColor
            }
          }
        },
        yaxis: {
          labels: {
            show: this.showValues,
            style: {
              colors: this.$store.state.ui.darkMode ? "white" : this.textColor
            }
          }
        },
        fill: {
          opacity: 1
        }
      }
    },
    generateColors(isDarkMode) {
      this.chart_options = {
        theme: {
          mode: isDarkMode ? "dark" : "light"
        },
        chart: {
          background: this.$store.state.ui.darkMode ? "#2b3c44" : "transparent"
        },
        grid: {
          borderColor: "#dbdbdb"
        },
        xaxis: {
          categories: this.labels.length ? this.formattedLables : this.formattedChartData,
          labels: {
            show: this.isChecked,
            style: {
              colors: isDarkMode ? "white" : this.textColor
            }
          }
        },
        yaxis: {
          labels: {
            show: this.showValues,
            style: {
              colors: isDarkMode ? "white" : this.textColor
            }
          }
        },
        dataLabels: {
          enabled: this.showDataLabels
        }
      }
    }
  }
}
</script>
